/**
 * Tipos da folha de pagamento dos PJ.
 *
 * Atenção à unidade: todos os campos monetários da folha chegam da API em
 * CENTAVOS (igual ao banco). Use `formatCents` de `@/lib/format` para exibir.
 */

export type FolhaStatus = "rascunho" | "fechada";

export interface FolhaResumo {
  id: string;
  competencia: string;
  status: FolhaStatus;
  franquiaAmil: number;
  totalColaboradores: number;
  totalLiquido: number;
  fechadaEm: string | null;
  reabertaCount: number;
  /** Fechadas: quantos estão com cadastro incompleto (badge "Fechada com pendência"). */
  pendenciasCadastro?: number;
}

export interface FolhaCompetencia {
  id: string;
  competencia: string;
  status: FolhaStatus;
  franquiaAmil: number;
  observacao: string | null;
  fechadaEm: string | null;
  reabertaCount: number;
  bloqueiosIgnorados: BloqueioIgnorado[] | null;
  lotes: FolhaLote[];
}

export interface FolhaLote {
  id: string;
  tipo: TipoPlanilha;
  arquivoNome: string;
  totalLinhas: number;
  casadas: number;
  naoCasadas: number;
  createdAt: string;
}

export interface FolhaItem {
  id: string;
  colaboradorId: string;
  nome: string;
  email: string;
  cpf: string | null;
  cnpj: string | null;
  razaoSocial: string | null;
  tomadorId: string | null;
  tomadorNome: string | null;
  tomadorCnpj: string | null;
  salarioFixo: number;
  comissoes: number;
  comissaoBase: number;
  bonusMeta: number;
  amilTitularBruto: number;
  amilFranquiaAplicada: number;
  amilTitularRepasse: number;
  /** Mensalidade dos dependentes — vem da planilha, se repete todo mês. */
  amilMensalidadeDep: number;
  /** Coparticipação usada por dependentes — vem da planilha, varia a cada mês. */
  amilCoparticipacaoDep: number;
  amilDependente: number;
  amilRepasseTotal: number;
  emprestimoParcela: number;
  outrosDescontos: number;
  ajusteManual: number;
  ajusteMotivo: string | null;
  liquido: number;
}

export interface FolhaTotais {
  salarioFixo: number;
  comissoes: number;
  comissaoBase: number;
  bonusMeta: number;
  amil: number;
  amilMensalidadeDep: number;
  amilCoparticipacaoDep: number;
  emprestimos: number;
  liquido: number;
}

export type CodigoBloqueio =
  | "PENDENCIAS_IMPORTACAO"
  | "LIQUIDO_NAO_POSITIVO"
  | "ITEM_SEM_TOMADOR"
  | "SEM_COPARTICIPACAO"
  | "HOLERITE_DIVERGENTE";

/** "Colaborador desligado" tem 21: motivo de ignorar precisa dizer alguma coisa. */
export const MOTIVO_IGNORAR_MINIMO = 20;

/** Título curto de cada bloqueio, para os diálogos de ignorar e a lista de ignorados. */
export const ROTULO_BLOQUEIO: Record<CodigoBloqueio, string> = {
  PENDENCIAS_IMPORTACAO: "Linhas de planilha sem colaborador",
  LIQUIDO_NAO_POSITIVO: "Líquido zerado ou negativo",
  ITEM_SEM_TOMADOR: "Colaborador sem tomador",
  SEM_COPARTICIPACAO: "Planilha da Amil Saúde não importada",
  HOLERITE_DIVERGENTE: "Holerite CLT com pendência",
};

/** Bloqueio de fechamento que o RH ignorou, com motivo. */
export interface BloqueioIgnorado {
  codigo: CodigoBloqueio;
  ids: string[];
  nomes: string[];
  motivo: string;
  porId: string;
  porNome: string | null;
  em: string;
}

/**
 * Cadastro incompleto para o ERP (PJ sem CNPJ...). Não impede o fechamento:
 * a folha fica "fechada com pendência" e o colaborador fica de fora do envio
 * ao ERP até completar o cadastro.
 */
export interface PendenciaCadastro {
  id: string;
  nome: string;
  detalhe: string;
}

export interface BloqueioFechamento {
  codigo: CodigoBloqueio;
  mensagem: string;
  colaboradores: { id: string; nome: string; detalhe?: string }[];
}

export interface FolhaDetalhe {
  folha: FolhaCompetencia;
  itens: FolhaItem[];
  totais: FolhaTotais;
  pendencias: number;
  bloqueios: BloqueioFechamento[];
  avisos: PendenciaCadastro[];
}

export type TipoPlanilha = "comissoes" | "amil_saude" | "amil_odonto";

/** Lote legado do PDF de coparticipação (importação removida; folhas antigas ainda têm). */
export const TIPO_COPARTICIPACAO = "amil_coparticipacao" as const;
/** Extrato Mensal da folha CLT (contabilidade). */
export const TIPO_EXTRATO_CLT = "extrato_clt" as const;
export type TipoLote = TipoPlanilha | typeof TIPO_COPARTICIPACAO | typeof TIPO_EXTRATO_CLT;

export const ROTULO_TIPO_PLANILHA: Record<TipoLote, string> = {
  comissoes: "Comissões",
  amil_saude: "Amil Saúde",
  amil_odonto: "Amil Odonto",
  amil_coparticipacao: "Amil — Coparticipação (PDF)",
  extrato_clt: "Extrato CLT",
};

export type MotivoPendencia =
  | "SEM_CHAVE"
  | "COLABORADOR_NAO_ENCONTRADO"
  | "AMBIGUO"
  | "VALOR_INVALIDO"
  | "VINCULO_PJ";

export interface Pendencia {
  id: string;
  loteId: string;
  linha: number;
  chaveEmail: string | null;
  chaveCpf: string | null;
  chaveCnpj: string | null;
  chaveNome: string | null;
  valor: number | null;
  /**
   * Só na Amil: coparticipação do titular e dos dependentes, cada uma da sua
   * coluna — a quebra vem pronta e não é escolhida à mão. A mensalidade de
   * dependentes é `valor` menos as duas.
   */
  valorTitular: number | null;
  valorDependente: number | null;
  motivo: MotivoPendencia;
  detalhe: string | null;
  lote?: { tipo: TipoLote; arquivoNome: string };
  /** Colaboradores parecidos, para o RH não caçar numa lista longa. */
  sugestoes?: SugestaoColaborador[];
}

/** Linha de planilha que o RH ignorou (ex.: colaborador já desligado). */
export interface PendenciaIgnorada extends Pendencia {
  ignoradoMotivo: string;
  ignoradoPorNome: string | null;
  resolvidoEm: string;
}

export interface SugestaoColaborador {
  colaboradorId: string;
  nome: string;
  email: string;
  score: number;
}

export interface ResultadoResolucao {
  resolvidas: number;
  chavesAprendidas: { tipo: string; valor: string }[];
  chavesRecusadas: { tipo: string; valor: string; motivo: string }[];
}

export interface VariacaoItem {
  colaboradorId: string;
  nome: string;
  liquido: number;
  liquidoAnterior: number | null;
  variacao: number | null;
  entrouAgora: boolean;
  saiuDaFolha: boolean;
}

export interface StatusEmissaoItem {
  colaboradorId: string;
  nome: string;
  email: string;
  liquido: number;
  nfStatus: string | null;
  nfNumero: string | null;
  nfValor: number | null;
  valorDivergente: boolean;
}

export interface StatusEmissao {
  competencia: string;
  fechada: boolean;
  total: number;
  emitiram: number;
  pendentes: number;
  itens: StatusEmissaoItem[];
}

export interface LinhaDivergente {
  linha: number;
  nome: string;
  comissao: number;
  bonusMeta: number;
  totalGeral: number;
  diferenca: number;
}

export interface ResultadoImportacao {
  loteId: string;
  tipo: TipoPlanilha;
  /** Onde o cabeçalho foi encontrado — planilhas costumam ter linha de título. */
  linhaCabecalho: number;
  totalLinhas: number;
  casadas: number;
  naoCasadas: number;
  /** Linhas de totais ou em branco, descartadas. */
  ignoradas: number;
  /**
   * Linhas com competência/data diferente da folha. Foram importadas mesmo
   * assim (comissão de outubro paga em novembro) — é só aviso de conferência.
   */
  outraCompetencia: number;
  competenciasDiferentes?: { competencia: string; linhas: number }[];
  /** Linhas sem valor a lançar. */
  semValor: number;
  /** Linhas de CLT ignoradas — para ele vale o extrato da contabilidade. */
  clt: number;
  totalImportado: number;
  divergencias: LinhaDivergente[];
  /** Comparação com a linha de totais do rodapé da planilha. */
  conferencia: Conferencia | null;
  pendencias: Pendencia[];
}

export interface Conferencia {
  totalPlanilha: number;
  totalImportado: number;
  diferenca: number;
  confere: boolean;
  /** Falso quando a planilha mistura competências. */
  comparavel: boolean;
}

export interface Demonstrativo {
  competencia: string;
  fechadaEm: string | null;
  franquiaAmil: number;
  tomadorNome: string | null;
  tomadorCnpj: string | null;
  salarioFixo: number;
  comissoes: number;
  comissaoBase: number;
  bonusMeta: number;
  amilTitularBruto: number;
  amilFranquiaAplicada: number;
  amilTitularRepasse: number;
  /** Mensalidade dos dependentes — vem da planilha, se repete todo mês. */
  amilMensalidadeDep: number;
  /** Coparticipação usada por dependentes — vem da planilha, varia a cada mês. */
  amilCoparticipacaoDep: number;
  amilDependente: number;
  amilRepasseTotal: number;
  emprestimoParcela: number;
  outrosDescontos: number;
  ajusteManual: number;
  ajusteMotivo: string | null;
  liquido: number;
  nf: { status: string; numero: string; valor: number } | null;
}

export interface DemonstrativoHistorico {
  competencia: string;
  fechadaEm: string | null;
  liquido: number;
}

// ---------------------------------------------------------------------------
// Empréstimos
// ---------------------------------------------------------------------------

export type ModalidadeEmprestimo = "parcelado_salario" | "bonificacao_metas";
export type CategoriaEmprestimo = "emprestimo" | "adiantamento" | "desconto";
export type StatusEmprestimo = "ativo" | "quitado" | "cancelado";
export type StatusParcela = "pendente" | "descontada" | "cancelada";

export interface EmprestimoParcela {
  id: string;
  numero: number;
  competencia: string;
  valor: number;
  status: StatusParcela;
}

export interface EmprestimoBaixa {
  id: string;
  tipo: "folha" | "bonificacao" | "manual";
  valor: number;
  data: string;
  observacao: string | null;
}

export interface Emprestimo {
  id: string;
  colaboradorId: string;
  colaborador?: { id: string; name: string; email: string };
  categoria: CategoriaEmprestimo;
  modalidade: ModalidadeEmprestimo;
  /** Quando o saldo da modalidade bonificação foi passado para parcelas. */
  parceladoEm: string | null;
  valorTotal: number;
  numeroParcelas: number | null;
  competenciaInicial: string | null;
  status: StatusEmprestimo;
  descricao: string | null;
  createdAt: string;
  parcelas: EmprestimoParcela[];
  baixas?: EmprestimoBaixa[];
  totalPago: number;
  saldo: number;
}

export const ROTULO_MODALIDADE: Record<ModalidadeEmprestimo, string> = {
  parcelado_salario: "Descontado do salário",
  bonificacao_metas: "Descontado da bonificação",
};

export const ROTULO_CATEGORIA: Record<CategoriaEmprestimo, string> = {
  emprestimo: "Empréstimo",
  adiantamento: "Adiantamento",
  desconto: "Desconto",
};

export const ROTULO_BAIXA: Record<EmprestimoBaixa["tipo"], string> = {
  folha: "Desconto em folha",
  bonificacao: "Desconto na bonificação",
  manual: "Baixa manual",
};

export const ROTULO_MOTIVO_PENDENCIA: Record<MotivoPendencia, string> = {
  SEM_CHAVE: "Linha sem identificação",
  COLABORADOR_NAO_ENCONTRADO: "Colaborador não encontrado",
  AMBIGUO: "Nome ambíguo",
  VALOR_INVALIDO: "Valor inválido",
  VINCULO_PJ: "Cadastrado como PJ",
};

// ---------------------------------------------------------------------------
// Holerite CLT — calculado pelo sistema; o do extrato da contabilidade fica
// só para conferência
// ---------------------------------------------------------------------------

export type TipoHolerite = "mensal" | "ferias" | "decimo_primeira" | "decimo_segunda";

export const ROTULO_TIPO_HOLERITE: Record<TipoHolerite, string> = {
  mensal: "Folha mensal",
  ferias: "Recibo de férias",
  decimo_primeira: "13º salário — 1ª parcela",
  decimo_segunda: "13º salário — 2ª parcela",
};

export interface HoleriteRubrica {
  id: string;
  ordem: number;
  codigo: string;
  descricao: string;
  /** Texto como no extrato: "31,00", "40,00", "32:12". */
  referencia: string | null;
  tipo: "provento" | "desconto";
  valor: number;
}

export interface HoleriteResumo {
  id: string;
  nome: string;
  cpf: string | null;
  codigoEmpregado: string;
  cargo: string | null;
  situacao: string | null;
  origem: "calculado" | "extrato";
  tipo: TipoHolerite;
  dataPagamento: string | null;
  proventos: number;
  descontos: number;
  liquido: number;
  valorFgts: number;
  /** No calculado: pendência (cadastro incompleto, erro, líquido negativo). */
  divergente: boolean;
  divergencias: string | null;
  avisos: string | null;
  /** Nulo enquanto a linha do extrato está pendente de associação. */
  colaborador: { id: string; name: string } | null;
  /** Líquido da folha mensal no extrato da contabilidade, para conferência. */
  liquidoContabilidade: number | null;
  /** Holerite mensal calculado de um colaborador: aceita o "Ajustar", igual à linha PJ. */
  ajustavel?: boolean;
  /** Centavos, com sinal. Entra no holerite mensal sem INSS, IRRF nem FGTS. */
  ajusteManual?: number;
  ajusteMotivo?: string | null;
  outrosDescontos?: number;
}

export interface Holerite {
  id: string;
  empresaNome: string;
  empresaCnpj: string | null;
  codigoEmpregado: string;
  nome: string;
  cpf: string | null;
  situacao: string | null;
  vinculoDescricao: string | null;
  admissao: string | null;
  cargo: string | null;
  cbo: string | null;
  departamento: string | null;
  centroCusto: string | null;
  filial: string | null;
  horasMes: string | null;
  salarioBase: number;
  observacao: string | null;
  proventos: number;
  descontos: number;
  liquido: number;
  baseInss: number;
  excedenteInss: number;
  baseFgts: number;
  valorFgts: number;
  baseIrrf: number;
  dependentesIrrf: number;
  dependentesSalFam: number;
  divergente: boolean;
  divergencias: string | null;
  origem: "calculado" | "extrato";
  tipo: TipoHolerite;
  dataPagamento: string | null;
  inss: number;
  irrf: number;
  avisos: string | null;
  rubricas: HoleriteRubrica[];
  folha: { competencia: string; status: string; fechadaEm: string | null };
}

export interface HoleriteHistorico {
  id: string;
  tipo: TipoHolerite;
  competencia: string;
  fechadaEm: string | null;
  liquido: number;
}

// --- Cadastro e eventos CLT ---

export interface ContratoClt {
  colaboradorId: string;
  admissao: string | null;
  cargo: string | null;
  cbo: string | null;
  jornadaMensalHoras: number;
  dependentesIrrf: number;
  filhosSalarioFamilia: number;
  /** Pontos-base: 4000 = 40%. */
  gratificacaoPct: number;
  /** Centavos. */
  valeTransporteMensal: number;
  aprendiz: boolean;
  fimExperiencia: string | null;
  desligamento: string | null;
}

export type TipoLancamentoClt =
  | "horas_extras_50"
  | "horas_extras_100"
  | "horas_noturnas"
  | "faltas"
  | "dsr_descontado"
  | "atrasos"
  | "comissao"
  | "premio"
  | "adiantamento"
  | "pensao"
  | "consignado"
  | "outro_provento"
  | "outro_desconto";

export const LANCAMENTOS_CLT: Record<TipoLancamentoClt, { rotulo: string; unidade: "horas" | "dias" | "valor" }> = {
  horas_extras_50: { rotulo: "Horas extras 50%", unidade: "horas" },
  horas_extras_100: { rotulo: "Horas extras 100%", unidade: "horas" },
  horas_noturnas: { rotulo: "Horas noturnas (adicional)", unidade: "horas" },
  faltas: { rotulo: "Faltas", unidade: "dias" },
  dsr_descontado: { rotulo: "DSR descontado", unidade: "dias" },
  atrasos: { rotulo: "Atrasos", unidade: "horas" },
  comissao: { rotulo: "Comissão (tributável)", unidade: "valor" },
  premio: { rotulo: "Prêmio (sem encargos)", unidade: "valor" },
  adiantamento: { rotulo: "Adiantamento salarial", unidade: "valor" },
  pensao: { rotulo: "Pensão alimentícia", unidade: "valor" },
  consignado: { rotulo: "Consignado (Crédito do Trabalhador)", unidade: "valor" },
  outro_provento: { rotulo: "Outro provento", unidade: "valor" },
  outro_desconto: { rotulo: "Outro desconto", unidade: "valor" },
};

export interface LancamentoClt {
  id: string;
  colaboradorId: string;
  colaborador: { id: string; name: string };
  tipo: TipoLancamentoClt;
  quantidade: number | null;
  /** Centavos. */
  valor: number | null;
  descricao: string | null;
  incideInss: boolean;
  incideIrrf: boolean;
  incideFgts: boolean;
}

export interface PeriodoAquisitivo {
  inicio: string;
  fim: string;
  limiteConcessao: string;
  emCurso: boolean;
  faltas: number;
  direito: number;
  gozados: number;
  abono: number;
  saldo: number;
  vencidoEmDobro: boolean;
}

export interface FeriasClt {
  id: string;
  periodoInicio: string;
  inicio: string;
  fim: string;
  dias: number;
  abonoDias: number;
  adiantar13: boolean;
  mediasInformadas: number | null;
  dataPagamento: string | null;
  dataPagamentoEfetiva: string;
  observacao: string | null;
}

export type TipoAfastamentoClt = "doenca" | "acidente_trabalho" | "maternidade" | "nao_remunerado";

export const ROTULO_AFASTAMENTO: Record<TipoAfastamentoClt, string> = {
  doenca: "Doença (auxílio-doença)",
  acidente_trabalho: "Acidente de trabalho",
  maternidade: "Licença-maternidade",
  nao_remunerado: "Licença não remunerada",
};

export interface AfastamentoClt {
  id: string;
  colaboradorId: string;
  tipo: TipoAfastamentoClt;
  inicio: string;
  fim: string | null;
  observacao: string | null;
}

export interface ResultadoExtratoClt {
  loteId: string;
  competencia: string;
  empresaNome: string;
  empregados: number;
  casados: number;
  naoCasados: number;
  vinculosAtualizados: { colaboradorId: string; nome: string }[];
  contratosCompletados: number;
  divergentes: { linha: number; nome: string; detalhes: string[] }[];
  conferencia: { totalLiquido: number; liquidoGeral: number | null; confere: boolean };
  pendencias: Pendencia[];
}
